import {
	Component,
	ViewChild,
	Input,
	ViewChildren,
	QueryList,
	AfterViewChecked,
	ChangeDetectorRef,
	HostListener,
	OnInit,
	NgZone
} from "@angular/core";
import { Table } from "primeng/table";
import { Calendar } from "primeng/primeng";
import { MatSlideToggleChange } from "@angular/material";

import * as moment from "moment";

import { GenericTableService } from "./generic-table.service";
import { GenericTable } from "./models";
import { TranslateService } from "../common/translate/translate.service";
import { SnackbarService } from "../common/snackbar/snackbar.service";

interface DateFieldDictionary {
	[key: string]: string | undefined;
}

@Component({
	selector: "sdp-generic-table",
	templateUrl: "./generic-table.component.html"
})
export class GenericTableComponent implements OnInit, AfterViewChecked {

	@Input()
	public columnSchema!: GenericTable.ColumnSchema[];

	@Input()
	public tableName!: string;

	private _tableData!: any[] | undefined;

	public isDataLoaded = false;

	@Input()
	public rows!: number;

	@Input()
	public rowSchema!: GenericTable.RowSchema;

	@Input()
	public disabled!: boolean;

	@ViewChild("tt", { static: false })
	public tt!: Table;

	@ViewChildren("calendar")
	public calendars!: QueryList<Calendar>;

	public totalRecords = "";
	public currentField = "";
	public now = new Date();
	public scrollHeight?: string = undefined;

	public datesGroup: DateFieldDictionary = {};

	private toggleGroup: GenericTable.ToggleBinding[] = [];

	constructor(
		private service: GenericTableService,
		private translateService: TranslateService,
		private changeDetectorRef: ChangeDetectorRef,
		private snackbarService: SnackbarService,
		private zone: NgZone
	) {
	}

	@HostListener("window:resize", ["$event"])
	public onResize(event: Event) {
		this.setScrollHeight();
	}

	get tableData(): any[] {
		if (this._tableData === undefined) {
			return this.service.initilizeTable(this.rows);
		} else {
			return this._tableData;
		}
	}

	@Input()
	set tableData(value: any[]) {
		this.isDataLoaded = !!value;
		this._tableData = value;
	}

	public ngOnInit() {
		this.setScrollHeight();
	}

	public async toggleChange(col: GenericTable.ToggleColumnSchema, row: any, event: MatSlideToggleChange) {
		if (!!col.bodySchema.callback) {
			/*
			* When a user clicks on a toggle, we need to hold off on changing its ui state. It is an async call
			* and we will then decide if the state needs to change
			*/
			this.toggleGroup.push({ boundField: col.bodySchema.boundField, row: row, newState: !event.checked });
			try {
				if (await col.bodySchema.callback(row, event)) {
					this.zone.run(() => {
						// this method is ran outside of angular. we need to run this specific line in angular. zone.run is that hook.
						// if the callback is successful we update the toggle to be true.
						// https://angular.io/api/core/NgZone#!#run-anchor
						this.toggleGroup.push({ boundField: col.bodySchema.boundField, row: row, newState: event.checked });
					});
				}
			} catch (e) {
				this.snackbarService.showErrorBar(e);
			}
		}
	}

	public ngAfterViewChecked() {
		this.pageChange();

		this.checkToggles();

		this.changeDetectorRef.detectChanges();
	}

	private checkToggles() {
		// if any toogle states need to be changed, do it here where we can use binding
		this.toggleGroup.forEach((state) => {
			state.row[state.boundField] = state.newState;
		});
		this.toggleGroup = [];
	}

	public filterChange() {
		this.pageChange();
	}

	public pageChange() {
		// if there are no records, the table will be undefined. We show a h4 element, not the table
		if (!!this.tt) {
			const total = !!this.tt.filteredValue ? this.tt.filteredValue.length : this.tt._totalRecords;
			const end = this.tt.first + this.tt.rows;
			this.totalRecords = this.translateService.translate("generic.totalRecords", {
				start: String(this.tt.first + 1),
				end: String(end <= total ? end : total),
				total: String(total),
				tableName: this.tableName
			});
		}
	}

	public doDateTimeChange(field: string, matchMode: GenericTable.DateTimeFilterDirection) {
		const mom = moment(this.datesGroup[field]);
		if (!!this.datesGroup[field] && moment.isMoment(mom)) {
			this.tt.filter(mom.valueOf(), field, matchMode);
		} else {
			this.resetDateTimeFilter(field, matchMode);
		}
	}

	public resetDateTimeFilter(field: string, matchMode: GenericTable.DateTimeFilterDirection) {
		this.datesGroup[field] = undefined;
		this.tt.filter(undefined, field, matchMode);
	}

	public closeCalendar(field: string, matchMode: GenericTable.DateTimeFilterDirection) {
		this.calendars.forEach((cal) => {
			if (cal.overlayVisible) {
				this.doDateTimeChange(field, matchMode);
				cal.overlayVisible = false;
			}
		});
	}

	public getTextContent(content: undefined | string | GenericTable.ContentCallback<string | undefined>, row: any) {
		let result: string | undefined;
		if (!!content) {
			if (typeof content === "function") {
				result = content(row);
			} else {
				result = content;
			}
		}
		if (!!result) {
			result = this.translateService.translate(result);
		}
		return result;
	}

	public getCSSContent(content: undefined | string | GenericTable.ContentCallback<string | undefined>, row: any) {
		let result: string | undefined;
		if (!!content) {
			if (typeof content === "function") {
				result = content(row);
			} else {
				result = content;
			}
		}
		return result;
	}

	private setScrollHeight() {
		// this offset includes the area above and below the display rows of the generic table
		const windowOffset = 430;
		const height = window.innerHeight - windowOffset;
		this.scrollHeight = `${height > 200 ? height : 200}px`;
	}

}
